import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Tab, Label, Dropdown, Media, Optional } from '@honda-canada/design-system-react';
import themeStyles from './styles/ProductLineTabs.styles';
import isSSR from '../../utils/isSSR';

const DesktopTabsWrapper = themeStyles.apply(Box, 'DesktopTabsWrapper');
const TabWrapper = themeStyles.apply(Box, 'TabWrapper');
const ProductLineTab = themeStyles.apply(Tab, 'ProductLineTab');
const DropdownLabel = themeStyles.apply(Label, 'DropdownLabel');
const MobileDropdownWrapper = themeStyles.apply(Box, 'MobileDropdownWrapper');

const PSP_PRODUCTLINE_TABS_SCHEMA = {
  MC: {
    name: 'motorcycle',
    text: 'Sub-apps.PriceAndFinance.motorcyclesLabel',
    label: 'Sub-apps.PriceAndFinance.motorcyclesLabel',
    value: 'MC',
    key: 'Motorcycle',
    ariaLabel: 'motorcycle-aria',
  },
  ATV: {
    name: 'atv',
    text: 'Sub-apps.PriceAndFinance.atvLabel',
    label: 'Sub-apps.PriceAndFinance.atvLabel',
    value: 'ATV',
    key: 'ATV',
    ariaLabel: 'atv-aria',
  },
  SXS: {
    name: 'side-by-side',
    text: 'Sub-apps.PriceAndFinance.sideBySideLabel',
    label: 'Sub-apps.PriceAndFinance.sideBySideLabel',
    value: 'SXS',
    key: 'Side-By-Side',
    ariaLabel: 'side-byside-aria',
  },
};

const ProductLineTabs = ({
  currentProductLine,
  setCurrentProductLine,
  resetSelectedOptions,
  availableProductLines,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const productLines = availableProductLines.map(productLine => ({
    ...PSP_PRODUCTLINE_TABS_SCHEMA[productLine],
    text: t(PSP_PRODUCTLINE_TABS_SCHEMA[productLine].text),
    label: t(PSP_PRODUCTLINE_TABS_SCHEMA[productLine].label),
  }));

  // derive activeTab from currentProductLine state
  const activeTab = productLines.findIndex(tab => tab?.value === currentProductLine) || 0;
  const { pathname } = (typeof window !== 'undefined' && window.location) || {};
  const tabsClickHandler = index => {
    setCurrentProductLine(productLines[index]?.value);
    // reset selected options
    resetSelectedOptions();
    history.replace(`${pathname}?vehicleType=${productLines[index]?.value}`);
  };
  const mobileTabsOnChangeHandler = productLine => {
    setCurrentProductLine(productLine);
    // reset selected options
    resetSelectedOptions();
    history.replace(`${pathname}?vehicleType=${productLine}`);
  };

  const vehicleTypeLabel = t('Sub-apps.PriceAndFinance.vehicleTypeLabel');

  return (
    <>
      <Media lessThan="desktop">
        <MobileDropdownWrapper data-testid="cy-productline-dropdown">
          <DropdownLabel htmlFor={currentProductLine}>{vehicleTypeLabel}</DropdownLabel>
          <Dropdown
            options={productLines}
            name={currentProductLine}
            ariaLabel={vehicleTypeLabel}
            placeholderText={t('Sub-apps.PriceAndFinance.motorcyclesLabel')}
            onChange={productLine => mobileTabsOnChangeHandler(productLine)}
            value={currentProductLine}
          />
        </MobileDropdownWrapper>
      </Media>
      <Media greaterThanOrEqual="desktop">
        <Optional when={!isSSR()}>
          <DesktopTabsWrapper>
            {productLines?.map((productLine, i) => (
              <TabWrapper
                key={productLine.key}
                isLastChild={i === productLines.length - 1}
                data-testid="cy-productline-tab"
              >
                <ProductLineTab
                  isActive={activeTab === i}
                  onClick={() => tabsClickHandler(i)}
                  data-testid={`cy-productline-tab-${productLine.value}`}
                  data-is-active-tab={activeTab === i}
                  style={{ paddingBottom: '21px' }}
                >
                  {productLine?.text}
                </ProductLineTab>
              </TabWrapper>
            ))}
          </DesktopTabsWrapper>
        </Optional>
      </Media>
    </>
  );
};

ProductLineTabs.propTypes = {
  currentProductLine: PropTypes.string.isRequired,
  setCurrentProductLine: PropTypes.func.isRequired,
  resetSelectedOptions: PropTypes.func.isRequired,
  availableProductLines: PropTypes.arrayOf(PropTypes.string),
};

export default ProductLineTabs;
